import { PageLoader } from "@/layouts/components/PageLoader/PageLoader";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { routes } from "../routes";
import { useLoan, type LoanContractSummary } from "@/features/loan";
import { useOfferRequest } from "@/features/offer";
import { useKyc } from "@/features/kyc";
import { useOnboarding } from "@/features/onboarding";
import type {
  LoanOfferState,
  OfferRequest,
} from "@/features/offer/types/offer";
import type { ApplicationState } from "@/features/application";
import { useApplication } from "@/features/application/hooks";
import { useKycSignalR } from "@/features/kyc/hooks";
import { useLoanOffer } from "@/features/offer/hooks";
import { hasAvailableOffer, hasCurrentFinalOffer } from "@/lib/offerUtils";

const freeRoutes = new Set<string>([
  routes.application.calculator,
  routes.application.calculatorTab("credit-line"),
  routes.application.calculatorTab("installment"),
  routes.application.creditLineAmount,
  routes.application.creditLinePaymentDate,
  routes.application.installmentConfirmation,
  routes.application.creditLineConfirmation,
  routes.application.derogationDeclaration,
  routes.application.alternativeOffer,
]);

export const ApplicationGuard = () => {
  const location = useLocation();
  useKycSignalR();

  const { isKycLoading, isKycValid } = useKyc(true);
  const { activeLoanSummary, isLoading: isLoanLoading } = useLoan(true);
  const { offer, isLoading: isOfferLoading } = useOfferRequest(true);
  const { isOnboardingStateLoading, isCompleted: isOnboardingCompleted } =
    useOnboarding(true);
  const { applicationState, isApplicationStateLoading } = useApplication(true);
  const { isLoading: isLoanOffersStateLoading, loanOfferState } =
    useLoanOffer(true);

  if (
    isOfferLoading ||
    isOnboardingStateLoading ||
    isLoanLoading ||
    isKycLoading ||
    isApplicationStateLoading ||
    isLoanOffersStateLoading
  ) {
    return <PageLoader />;
  }

  const nextStep = getNextApplicationStep(
    isKycValid,
    activeLoanSummary,
    isOnboardingCompleted,
    offer,
    applicationState,
    loanOfferState,
  );

  // allow navigate inside the application routes only if there is not a required action (ex.: KYC)
  const isFreeRoute =
    freeRoutes.has(location.pathname) &&
    nextStep.startsWith(routes.application.base);

  if (location.pathname !== nextStep && !isFreeRoute) {
    return <Navigate to={nextStep} replace />;
  }

  return <Outlet />;
};

const getNextApplicationStep = (
  isKycValid: boolean,
  activeLoanSummary: LoanContractSummary | null | undefined,
  isOnboardingCompleted: boolean,
  offer: OfferRequest | undefined,
  application: ApplicationState | undefined,
  loanOfferState: LoanOfferState | undefined,
) => {
  if (activeLoanSummary) {
    return routes.loan.base;
  }

  if (!isOnboardingCompleted) return routes.onboarding.base;

  if (!isKycValid) return routes.kyc.base;

  // NoOfferAvailable is ignored while an available offer exists, matching
  // OfferGuard - otherwise the two bounce the user between /offer and here.
  // A current final offer settles it, as OfferGuard has it.
  const isOfferUnresolved =
    !hasCurrentFinalOffer(application) &&
    offer &&
    offer.status !== "Complete" &&
    !(
      offer.status === "NoOfferAvailable" &&
      hasAvailableOffer(loanOfferState?.creditOffers)
    );

  if (isOfferUnresolved) {
    return routes.offer.base;
  }

  if (loanOfferState?.proofOfIncomeProcessing) {
    return routes.offer.proofOfIncome;
  }

  if (
    application?.hasFinalOffer &&
    !hasAvailableOffer(loanOfferState?.creditOffers)
  ) {
    return routes.offer.notAvailable;
  }

  return routes.application.base;
};
